import React, { useCallback, useEffect, useState } from "react";
import { Download, Eye, Paperclip, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

export const ACCEPTED_ATTACHMENTS = ".pdf,.jpg,.jpeg,.png";
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const readableSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export const AttachmentsPanel = ({
  invoice,
  canDelete = true,
  canUpload = true,
  autoPreview = false,
  previewHeight = 420,
  onChanged,
}) => {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    if (!invoice) return;
    try {
      const { data } = await api.get(`/invoices/${invoice.id}/attachments`);
      setFiles(data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  }, [invoice]);

  useEffect(() => { load(); }, [load]);

  const openPreview = useCallback(async (attachment) => {
    try {
      const { data } = await api.get(`/attachments/${attachment.id}/download`, { responseType: "blob" });
      const blob = new Blob([data], { type: attachment.content_type });
      setPreview((current) => {
        if (current?.url) URL.revokeObjectURL(current.url);
        return { id: attachment.id, url: URL.createObjectURL(blob), attachment };
      });
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Preview unavailable");
    }
  }, []);

  useEffect(() => {
    if (autoPreview && files.length && !preview) openPreview(files[0]);
  }, [autoPreview, files, preview, openPreview]);

  useEffect(() => () => { if (preview?.url) URL.revokeObjectURL(preview.url); }, [preview]);

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_BYTES) return toast.error("Attachments must be 10 MB or smaller");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/invoices/${invoice.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} attached`);
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (attachment) => {
    if (!window.confirm(`Remove ${attachment.original_filename}?`)) return;
    try {
      await api.delete(`/attachments/${attachment.id}`);
      toast.success("Attachment removed");
      if (preview?.id === attachment.id) setPreview(null);
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const download = async (attachment) => {
    try {
      await downloadFile(`/attachments/${attachment.id}/download`, attachment.original_filename);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-3">
      <div className="divide-y border" style={{ borderColor: "var(--border)" }} data-testid="attachment-list">
        {files.map((attachment) => (
          <div
            key={attachment.id}
            className="flex items-center gap-3 px-4 py-3 text-sm"
            style={preview?.id === attachment.id ? { background: "var(--surface)" } : {}}
            data-testid={`attachment-${attachment.id}`}
          >
            <Paperclip size={14} style={{ color: "var(--muted)" }} />
            <div className="flex-1 min-w-0">
              <div className="truncate font-medium" style={{ color: "var(--ink)" }}>{attachment.original_filename}</div>
              <div className="text-xs" style={{ color: "var(--muted)" }}>{readableSize(attachment.size)}</div>
            </div>
            <button type="button" onClick={() => openPreview(attachment)} className="btn-ghost !p-2" aria-label="Preview" data-testid={`preview-attachment-${attachment.id}`}>
              <Eye size={14} />
            </button>
            <button type="button" onClick={() => download(attachment)} className="btn-ghost !p-2" aria-label="Download" data-testid={`download-attachment-${attachment.id}`}>
              <Download size={14} />
            </button>
            {canDelete && (
              <button type="button" onClick={() => remove(attachment)} className="btn-ghost !p-2" aria-label="Remove" data-testid={`delete-attachment-${attachment.id}`}>
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
        {!files.length && (
          <div className="px-4 py-8 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="attachments-empty">
            No documents attached yet.
          </div>
        )}
      </div>

      {preview && (
        <div className="border" style={{ borderColor: "var(--border)" }} data-testid="attachment-preview">
          {preview.attachment.content_type === "application/pdf" ? (
            <iframe
              title={preview.attachment.original_filename}
              src={preview.url}
              className="w-full bg-white"
              style={{ height: previewHeight, border: 0 }}
              data-testid="attachment-preview-frame"
            />
          ) : (
            <img
              src={preview.url}
              alt={preview.attachment.original_filename}
              className="w-full object-contain bg-white"
              style={{ maxHeight: previewHeight }}
              data-testid="attachment-preview-image"
            />
          )}
        </div>
      )}

      {canUpload && (
        <>
          <label className="btn-primary flex items-center justify-center gap-2 cursor-pointer" data-testid="attachment-upload-label">
            <Upload size={14} /> {busy ? "Uploading…" : "Attach PDF or image"}
            <input type="file" className="hidden" accept={ACCEPTED_ATTACHMENTS} onChange={upload} disabled={busy} data-testid="attachment-upload-input" />
          </label>
          <p className="text-xs" style={{ color: "var(--muted)" }}>PDF, JPG or PNG · up to 10 MB each</p>
        </>
      )}
    </div>
  );
};

export const AttachmentsDialog = ({ invoice, canDelete = true, onClose, onChanged }) => (
  <Dialog open={Boolean(invoice)} onOpenChange={(isOpen) => !isOpen && onClose()}>
    <DialogContent className="!rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" style={{ borderColor: "var(--border)" }} data-testid="attachments-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading font-black text-2xl tracking-tight">Invoice documents</DialogTitle>
        <DialogDescription>
          {invoice?.vendor_name} · {invoice?.invoice_number}
        </DialogDescription>
      </DialogHeader>
      <AttachmentsPanel invoice={invoice} canDelete={canDelete} onChanged={onChanged} />
    </DialogContent>
  </Dialog>
);
